import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import "./Problem.css";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {getNodeText, getProblemMeta, postProblemSolution} from "../../api/Client";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import GradeResult from "./GradeResult";
import {useStreamedText} from "../../hooks/useStreamedText";
import {splitNodeText} from "../../utils/nodeText";
import AnswerEditor, {AnswerEditorType, SmartButton} from "./AnswerEditor";

const ANSWER_EDITOR_TYPES: AnswerEditorType[] = ["text", "code", "math", "smart"];

export default function Problem() {
    const navigate = useNavigate();

    const [answer, setAnswer] = useState("");

    const { jobId, problemId } = useParams();

    const queryClient = useQueryClient();

    const {loading, streaming, text, error: streamError} = useStreamedText(problemId, getNodeText);

    const {intro, problem} = useMemo(() => splitNodeText(text), [text]);

    const {data, isLoading} = useQuery({
        queryKey: ["problem", problemId],
        queryFn: () => getProblemMeta(problemId!),
        enabled: !!problemId,
    });

    // The backend computes answerFormat/answerLanguage (via generateProblemMeta)
    // right after the problem text finishes generating, as part of the same
    // request the text streamed over — so once `streaming` flips from true to
    // false, that metadata is guaranteed to be sitting in the DB. The meta
    // query above was fetched (and cached) before generation necessarily
    // finished, so it's very likely still holding the node's default
    // ("text") answerFormat — refetch once generation completes to pick up
    // the real one, instead of leaving the answer field on the wrong widget.
    const wasStreamingRef = useRef(streaming);
    useEffect(() => {
        if (wasStreamingRef.current && !streaming) {
            queryClient.invalidateQueries({queryKey: ["problem", problemId]});
        }
        wasStreamingRef.current = streaming;
    }, [streaming, problemId, queryClient]);

    const [showGrade, setShowGrade] = useState(true);

    // Problem's the same component instance across problemId changes (the
    // route just re-renders it with new params) — reset back to showing the
    // grade whenever we land on a (possibly different) graded answer.
    useEffect(() => {
        setShowGrade(true);
    }, [problemId, data?.answer]);

    const solutionMutation = useMutation({
        mutationFn: (solution: string) => postProblemSolution(problemId!, solution),
        onSuccess: (res, solution) => {
            // The POST response carries the grade but not the answer, and GradeResult
            // is gated on `answer` being set — so merge the submitted answer back in.
            queryClient.setQueryData(["problem", problemId], (prev: any) => ({
                ...prev,
                answer: solution,
                answerGrade: res.answerGrade,
                answerNotes: res.answerNotes,
            }));
        },
    });

    // Backend-controlled per-problem: which widget the answer field uses.
    // Falls back to "text" for old/missing/unrecognized values so this never
    // breaks on responses from before the field existed.
    const answerType: AnswerEditorType = ANSWER_EDITOR_TYPES.includes(data?.answerFormat)
        ? data.answerFormat
        : "text";

    // Monaco language id (e.g. "python", "javascript") — only meaningful
    // when answerType is "code"; AnswerEditor falls back to plaintext
    // highlighting on its own if this is missing.
    const answerLanguage: string | undefined = data?.answerLanguage;
    // Per-problem insert-math buttons for the "smart" editor — not sent by
    // the backend yet (see notes on AnswerEditor.tsx), so this is always
    // undefined today and AnswerEditor falls back to its built-in default
    // set. Wired up now so nothing else needs to change once it exists.
    const answerSmartButtons: SmartButton[] | undefined = Array.isArray(data?.answerSmartButtons)
        ? data.answerSmartButtons
        : undefined;

    function submitAnswer() {
        if (problemId && answer.length > 0) {
            solutionMutation.mutate(answer);
        }
    }

    const isAnswered = data?.answer != null;

    return (
        <>
            {data?.answer != null && showGrade && (
                <GradeResult
                    grade={data?.answerGrade}
                    notes={data?.answerNotes ?? ""}
                    onContinue={() => navigate(`/learn/${jobId}`)}
                    onReviewAnswer={() => setShowGrade(false)}
                />
            )}
            <div className="problem-page">
                <header className="problem-page__header">
                    <div>
                        <h1>{data?.nodeTitle || "Problem"}</h1>
                        <span className="problem-page__badge">{data?.groupTitle || "Group"}</span>
                    </div>

                    <button
                        className="problem-page__exit"
                        onClick={() => navigate(`/learn/${jobId}`)}
                        aria-label="Exit problem"
                    >
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <path
                                d="M6 6 L18 18 M18 6 L6 18"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </header>

                <main className="problem-page__content">
                    {loading ? (
                        <section className="problem-card">
                            <div className="problem-card__text">
                                <div className="problem-card__skeleton" aria-live="polite" aria-busy="true">
                                    <span className="problem-card__skeleton-line" />
                                    <span className="problem-card__skeleton-line" />
                                    <span className="problem-card__skeleton-line problem-card__skeleton-line--short" />
                                </div>
                            </div>
                        </section>
                    ) : streamError ? (
                        <section className="problem-card">
                            <p className="problem-answer__error" role="alert">{streamError}</p>
                        </section>
                    ) : (
                        <>
                            {intro && (
                                <section className="problem-card problem-card--intro">
                                    <div className="problem-card__text">
                                        <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                                            {intro}
                                        </ReactMarkdown>
                                    </div>
                                </section>
                            )}
                            <section className="problem-card problem-card--problem">
                                <div className="problem-card__text">
                                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                                        {problem}
                                    </ReactMarkdown>
                                </div>
                            </section>
                        </>
                    )}

                    <section className="problem-answer">
                        <label htmlFor="answer">Your Answer</label>

                        {isAnswered ? (
                            <div className="problem-answer__display" id="answer">
                                <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {data.answer}
                                </ReactMarkdown>
                            </div>
                        ) : (
                            <AnswerEditor
                                type={answerType}
                                language={answerLanguage}
                                smartButtons={answerSmartButtons}
                                value={answer}
                                onChange={setAnswer}
                                placeholder="Explain your reasoning or enter your final answer..."
                            />
                        )}
                    </section>
                </main>

                <footer className="problem-page__footer">
                    {solutionMutation.isError && (
                        <p className="problem-answer__error" role="alert">
                            Couldn't grade that answer. Try submitting again.
                        </p>
                    )}

                    {isAnswered ? (
                        <button
                            onClick={() => setShowGrade(true)}
                            className="button button--primary"
                        >
                            Show Grade
                        </button>
                    ) : (
                        <button
                            onClick={submitAnswer}
                            className="button button--primary"
                            disabled={solutionMutation.isPending || answer.length === 0}
                        >
                            {solutionMutation.isPending ? "Grading..." : "Submit Answer"}
                        </button>
                    )}
                </footer>
            </div>
        </>
    );
}